import {defineStore} from "pinia";
import {api} from "../scripts/api";
import {reactive, ref, type Ref} from "vue";
import type {AxiosRequestConfig, AxiosResponse} from "axios";
import {type OfferResponse} from "../../responses/OfferResponse";
import mitt, {type Emitter} from "mitt";
import {merge} from "lodash";
import type {UnwrapNestedRefs} from 'vue';
import {onMounted} from "vue";
import {isIframe} from "../../utils/iframe";

type Events = {
    afterAddingOffer: void
    refreshActiveOffer: void
}

export const useOfferStore = defineStore('offer', () => {
    const isLoadingOverview: Ref<boolean> = ref(true);
    const isLoadingOffer: Ref<boolean> = ref(false);
    const events: Emitter<Events> = mitt<Events>();
    const activeOffer: Ref<OfferResponse|null> = ref(null);

    const loadOfferAbortController: Ref<AbortController | null> = ref(null);

    const updateQuantityRequest: UnwrapNestedRefs<{
        timeout: number | null
        abort: AbortController | null
    }> = reactive({
        timeout: null,
        abort: null,
    })

    const addItem = (payload) => {
        return new Promise((resolve, reject) => {
            api().post("/site/offer/line/", payload)
                .then(response => {
                    resolve(response);

                    events.emit('afterAddingOffer');
                    loadOffer(false);
                })
                .catch(error => {
                    reject(error);
                });
        });
    }
    const createOffer = (payload) => {
        return new Promise((resolve, reject) => {
            api().post("/site/form/offer/", payload)
                .then(response => {
                    resolve(response);

                    loadOffer();
                })
                .catch(error => {
                    reject(error);
                });
        });
    }

    const updateOffer = () => {
        return new Promise((resolve, reject) => {
            api().post('/site/offers/' + activeOffer.value?.id + '/update/', {
                name: activeOffer.value?.name,
            })
                .then(response => {
                    resolve(response);

                    loadOffer();
                })
                .catch(error => {
                    reject(error);
                });
        });
    }

    const deleteOffer = (id) => {
        return new Promise(async (resolve, reject): Promise<void> => {
            await api().post('/site/offers/' + id + '/delete/', {_method: "DELETE"})
                .then((response) => {
                    resolve(response.data);
                }).catch((error) => {
                    if (error.response) {
                        resolve(error.response.data)
                    } else {
                        reject(error)
                    }
                });
        });
    }

    const loadOffer = async (reloadOverview = true) => {
        return new Promise(async (resolve, reject) => {
            if(reloadOverview) isLoadingOverview.value = true;

            if (loadOfferAbortController.value !== null) {
                loadOfferAbortController.value.abort()
            }

            const abortController: AbortController = new AbortController()
            loadOfferAbortController.value = abortController

            try {
                const config: AxiosRequestConfig = {
                    signal: abortController.signal
                }

                const response: void | AxiosResponse<{

                }> = await api().get(
                    '/site/offers/',
                    config
                ).then(response => {
                    resolve(response.data);
                    activeOffer.value = response.data.activeOffer;
                    isLoadingOverview.value = false;
                })
            } catch (e) {
                isLoadingOverview.value = false

                reject(e)
            }
        });
    }

    const updateQuantity = async <T>(payload): Promise<T> => {
        return new Promise<T>(async (resolve, reject): Promise<void> => {
            isLoadingOffer.value = true;
            if (updateQuantityRequest.abort !== null) {
                updateQuantityRequest.abort.abort()
            }

            const abortController: AbortController = new AbortController()
            updateQuantityRequest.abort = abortController

            try {
                const config: AxiosRequestConfig = {
                    signal: abortController.signal
                }

                const response: void | AxiosResponse<T> = await api().post(
                    '/site/offer/line/update-quantity/',
                    merge(payload, {_method: "PUT"}),
                    config
                ).then(response => {
                    resolve(response.data);

                    isLoadingOffer.value = false;
                    loadOffer(false);
                })
            } catch (e) {
                reject(e)
            }

        });
    }

    const deleteOfferLine = (id) => {
        return new Promise(async (resolve, reject): Promise<void> => {
            isLoadingOffer.value = true;
            await api().post('/site/offer/line/delete/', {
                offer_line_id: id,
                _method: "DELETE"
            })
                .then((response) => {
                    resolve(response.data);

                    loadOffer();
                    isLoadingOffer.value = false;
                }).catch((error) => {
                    resolve(error.response.data);
                    reject(error);
                });
        });
    }

    onMounted(async () => {
        if (!isIframe() && window.logged_in === 1) {
            await loadOffer()
        }
    })

    return {
        isLoadingOverview,
        isLoadingOffer,
        events,
        activeOffer,
        addItem,
        deleteOfferLine,
        createOffer,
        updateOffer,
        deleteOffer,
        loadOffer,
        updateQuantity,
    }
});
